import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Calendar, Pencil, Search, X } from "lucide-react";
import { api } from "../api";
import { useTranslation } from "../i18n";
import { isChoosable } from "../lib/collaboratorState";
import { useTaxonomy } from "../hooks/useTaxonomy";
// 58 §5c/§5b — the local `isWeekend` is gone. What a non-working day IS is
// now the firm's answer (src/lib/scheduleAxis.ts), not Saturday-or-Sunday.
import {
  SCHED_SLOTS,
  SCHED_START,
  slotToTime,
  pname,
  coIni,
  queueScrollToEv,
} from "../utils";
import { axisDayOf, isNonWorkingDow } from "../lib/scheduleAxis";
import { ModalLayout } from "../components/SharedUI";
import { CT, RAD, FS, Z } from "../lib/tokens";
import MenuSelect from "../components/MenuSelect"; // 154B §2
import { useConfirm } from "../components/useConfirmAndUndo";

// Phase 19 — Places lateral view.
//
// Reuses the SCHEDULE idiom (module-level paint state, capture-phase
// window mouseup, optimistic-with-rollback, cursor-anchored drag
// tooltip) but NOT the ScheduleView/ScheduleGrid components — those
// are too schedule-specific (work-location taxonomy, collaborator
// axis, 5-day week, no ACL).
//
// Layout:
//   ── Section controls (responsive row): search · Book · Hold · My ──
//   ── Per-category sections, vertically stacked ──
//      ── per category: header + paginate-fwd, then for each place ──
//         ── per place: name/location/capacity header + mini-grid ──
//            Mini-grid columns = 15 days; rows = SCHED_SLOTS half-hours.
//            Dimmed base-availability rendered UNDER reservation cells.

const WINDOW_DAYS = 15;
const OVERVIEW_DAYS = 8;

// Consecutive calendar days starting at `start`, length `n` — weekends
// INCLUDED (rendered narrower so week boundaries read at a glance). Used by
// the collective overview (day-major).
function calendarDays(start: any, n: any) {
  const out = [];
  const cursor = new Date(start);
  for (let i = 0; i < n; i++) {
    out.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}


// Half-width weekend columns in the availability overview can't fit a full
// place name, so they show an auto-derived abbreviation: the uppercased first
// character of each whitespace-separated word ("Black Room"→"BR", "Lounge"→"L",
// "Poste 1"→"P1"). Pure display — no stored field, no migration.
function abbrevName(name: any) {
  return String(name || "")
    .trim()
    .split(/\s+/)
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase();
}

// Phase 19 r9 — instant custom tooltip. The blocks previously used the native
// HTML `title` attribute, whose ~1s browser-imposed appearance delay made the
// (already-loaded) reservation info feel slow. This hook drives a fixed,
// cursor-anchored tooltip that appears immediately on hover. The data is the
// same string the `title` carried — no extra fetch. Set on mouseenter only
// (not mousemove) so hovering a large grid doesn't re-render per pixel.
function useHoverTip() {
  const [tip, setTip] = useState<any>(null); // { x, y, text }
  const bind = useCallback((text: any) => {
    if (!text) return {};
    return {
      onMouseEnter: (e: any) => setTip({ x: e.clientX, y: e.clientY, text }),
      onMouseLeave: () => setTip(null),
    };
  }, []);
  return { tip, bind, hideTip: () => setTip(null) };
}

function HoverTip({ tip }: any) {
  if (!tip) return null;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const left = Math.max(6, Math.min(tip.x + 12, vw - 286));
  const top = Math.min(tip.y + 16, vh - 60);
  return (
    <div
      style={{
        position: "fixed",
        left,
        top,
        maxWidth: 280,
        background: `var(--bs, ${CT.c_141820})`,
        border: "1px solid var(--br)",
        borderRadius: RAD.r6,
        padding: "5px 8px",
        fontSize: FS.s11,
        color: "var(--tp)",
        pointerEvents: "none",
        zIndex: Z.picker,
        boxShadow: "0 4px 14px rgba(0,0,0,.45)",
        whiteSpace: "pre-wrap",
        lineHeight: 1.35,
      }}
    >
      {tip.text}
    </div>
  );
}

// ── Module-level paint state ─────────────────────────────────────────
let _painting = false;
let _paintDrag: any = null; // { placeId, dayIdx, startSlot, currentSlot, mouseX, mouseY }
let _paintCommitFn: any = null;
let _rerenderFn: any = null;

function handleMouseUp() {
  if (_painting) {
    _painting = false;
    const drag = _paintDrag;
    _paintDrag = null;
    if (_rerenderFn) _rerenderFn();
    if (drag && _paintCommitFn) {
      // Commit the paint stroke at mouseup — open the reserve modal with
      // the painted range pre-filled. The actual reservation create is
      // gated by the modal confirm; this only seeds the form.
      _paintCommitFn(drag);
    }
  }
}

// Collective-overview paint state — separate from the detail's so the two
// grids never cross-trigger. Drag spans slots within ONE (place, day) column.
let _ovPainting = false;
let _ovDrag: any = null; // { placeId, iso, startSlot, currentSlot }
let _ovCommitFn: any = null;
let _ovRerenderFn: any = null;

function handleOvMouseUp() {
  if (_ovPainting) {
    _ovPainting = false;
    const drag = _ovDrag;
    _ovDrag = null;
    if (_ovRerenderFn) _ovRerenderFn();
    if (drag && _ovCommitFn) _ovCommitFn(drag);
  }
}

function toIsoDate(d: any) {
  // Local-time YYYY-MM-DD; avoids the .toISOString() UTC-offset bug.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDays(d: any, n: any) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function isoDow(d: any) {
  // 0=Mon..6=Sun to match baseAvailability indexing.
  return (d.getDay() + 6) % 7;
}

function slotsOverlap(aS: any, aE: any, bS: any, bE: any) {
  return aS < bE && bS < aE;
}

// Selected-state styling for a `.bt bg` toggle — accent fill, same size as
// the unselected button (so the toolbar doesn't reflow on toggle).
function activeToggleStyle(active: any) {
  return active
    ? { background: "var(--as)", color: "var(--ac)", borderColor: "var(--ac)" }
    : undefined;
}

// Interval-graph lane assignment so OVERLAPPING reservations on one day
// render side-by-side (a visible clash) instead of stacking/hiding. Each
// reservation gets a { lane, clash } — clash=true when it intersects any
// other reservation that same day. laneCount drives the column split.
function assignLanes(dayRes: any) {
  const sorted = [...dayRes].sort(
    (a, b) => a.startSlot - b.startSlot || a.endSlot - b.endSlot,
  );
  const laneEnds: any[] = []; // exclusive end slot currently occupying each lane
  const items = [];
  for (const r of sorted) {
    let lane = laneEnds.findIndex((end) => end <= r.startSlot);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(r.endSlot);
    } else {
      laneEnds[lane] = r.endSlot;
    }
    const clash = sorted.some(
      (o) => o !== r && slotsOverlap(r.startSlot, r.endSlot, o.startSlot, o.endSlot),
    );
    items.push({ res: r, lane, clash });
  }
  return { laneCount: Math.max(1, laneEnds.length), items };
}

/* BRIEF 124 §3 — the two shapes the jump needs, written out rather than `any`.
   `EventRow` is the subset of an agenda event this modal reads; `LinkedEventRef`
   is what `openLinkedEvent` (:1794) consumes. Both optional-everything, because
   the row arrives from two places — the locally-loaded agenda set and the API's
   inline `event` — and the two shapes are one type. */
type EventRow = {
  id?: number | string | null;
  title?: string | null;
  date?: string | null;
  caseId?: number | null;
  caId?: number | null;
  ccId?: number | null;
};
type LinkedEventRef = {
  id: number | string | null | undefined;
  caseId: number | null;
  ccId: number | null;
};

// ── Reserve / edit modal ─────────────────────────────────────────────
function ReserveModal({
  initial, // { placeId, date, startSlot, endSlot } seeded from paint or My-edit
  places,
  collaborators,
  events, // agenda events for the "Select event" picker
  currentUserId,
  defaultState, // "held" | "booked"
  isEdit, // editing an existing reservation
  onClose,
  onConfirm,
  conflictPayload, // { conflicts } when re-opened after 409
  viewerCanOverride, // bool — controls Override button visibility
  /* ── BRIEF 124 §3 — JUMP TO THE LINKED EVENT ────────────────────────────
     Laurent: "when checking the reservation, one should be able to click on
     the linked Event to jump to it in CV/CCV, highlighted."

     [MEASURED] the seam was already built and already had callers — the room
     GRID jumps (`onOpenEvent`, :943 and :982) through `openLinkedEvent`
     (:1794), which is itself the shape `navAgendaEvent` (in src/App.tsx) copied.
     What had no read side was THE RESERVATION ITSELF: this modal lets you PICK
     an event and offered no way to GO to it.

     So this prop is `openLinkedEvent`, unchanged, handed one floor down. No new
     navigation and no new highlight machinery — §3's fence forbids both, and
     writing either would have meant missing the seam. */
  onOpenEvent, // (linkedEvent) => void — the SAME callback the grid rows use
}: any) {
  const { t, locale } = useTranslation();
  const [date, setDate] = useState(initial.date);
  const [startSlot, setStartSlot] = useState(initial.startSlot);
  const [endSlot, setEndSlot] = useState(initial.endSlot);
  const [state, setState] = useState(initial.state || defaultState || "booked");
  const [notes, setNotes] = useState(initial.notes || "");
  const [subject, setSubject] = useState(initial.subject || "");
  const [eventId, setEventId] = useState(initial.eventId || null);
  const [notify, setNotify] = useState<any[]>([]); // chosen collaborator IDs
  const [busy, setBusy] = useState(false);
  const place = places.find((p: any) => p.id === initial.placeId);

  const eventTitle = (id: any) => {
    const e = (events || []).find((x: any) => x.id === id);
    return e ? e.title || t("places.reserve.untitledEvent", { fallback: "(untitled event)" }) : null;
  };
  /* BRIEF 124 §3 — the three fields `openLinkedEvent` reads, and no more.
     `caId` OR `caseId`, because an agenda event carries both spellings in this
     tree depending on where it came from — the same defensive read
     `linkedEventOf` (:576) and `navAgendaEvent` (in src/App.tsx) both make.

     TYPED RATHER THAN `any`, though every neighbour in this file is `any`: the
     ratchet may only shrink, and new code does not get to pay for old habits
     (SESSION_RULES). The shape is exactly what the jump consumes. */
  const linkedEventForJump = (id: number | string | null): LinkedEventRef | null => {
    const e = (events || []).find((x: EventRow) => x.id === id);
    if (!e) return null;
    return {
      id: e.id,
      caseId: e.caseId != null ? e.caseId : e.caId != null ? e.caId : null,
      ccId: e.ccId != null ? e.ccId : null,
    };
  };
  const fmtDay = (d: any) => {
    try {
      return new Date(d).toLocaleDateString(locale, { day: "2-digit", month: "short" });
    } catch {
      return String(d || "");
    }
  };
  // Events sorted by date proximity to the reservation date (most relevant
  // first). Exact filtering is left for Laurent to refine at smoke.
  const sortedEvents = useMemo(() => {
    const ref = date ? new Date(date + "T00:00:00").getTime() : Date.parse(initial.date) || 0;
    return (events || [])
      .filter((e: any) => e && e.date)
      .map((e: any) => ({ e, delta: Math.abs(new Date(e.date).getTime() - ref) }))
      .sort((a: any, b: any) => a.delta - b.delta)
      .slice(0, 60)
      .map((x: any) => x.e);
  }, [events, date, initial.date]);

  const submit = async (overrideFlag: any) => {
    if (endSlot <= startSlot) return;
    setBusy(true);
    try {
      await onConfirm({
        date,
        startSlot,
        endSlot,
        state,
        notes,
        subject,
        eventId,
        notifyCollaboratorIds: notify,
        override: !!overrideFlag,
      });
    } finally {
      setBusy(false);
    }
  };
  const slotsOptions = useMemo(
    () => Array.from({ length: SCHED_SLOTS + 1 }, (_, i) => i),
    [],
  );
  const hasConflicts = conflictPayload && Array.isArray(conflictPayload.conflicts) && conflictPayload.conflicts.length > 0;

  const footer = (
    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, padding: "12px 16px", borderTop: "1px solid var(--br)" }}>
      <button className="btn btn-ghost" type="button" onClick={onClose} disabled={busy}>
        {t("common.cancel", { fallback: "Cancel" })}
      </button>
      {hasConflicts ? (
        viewerCanOverride ? (
          <button className="btn btn-danger" type="button" onClick={() => submit(true)} disabled={busy}>
            {t("places.reserve.override", { fallback: "Override (bump)" })}
          </button>
        ) : (
          <span style={{ fontSize: FS.s11, color: `var(--rd, ${CT.c_dc2626})`, alignSelf: "center", marginRight: 6 }}>
            {t("places.reserve.no_override", {
              fallback: "You don't have authority to override — pick another slot.",
            })}
          </span>
        )
      ) : (
        <button className="btn btn-primary" type="button" onClick={() => submit(false)} disabled={busy} style={{ padding: "4px 12px", fontSize: FS.s11 }}>
          {isEdit ? t("common.save", { fallback: "Save" }) : t("places.reserve.confirm", { fallback: "Reserve" })}
        </button>
      )}
    </div>
  );

  return (
    <ModalLayout {...({} as any)}
      title={
        (isEdit
          ? t("places.reserve.editTitle", { fallback: "Edit reservation" })
          : t("places.reserve.title", { fallback: "Reserve" })) + (place ? " — " + place.name : "")
      }
      onClose={onClose}
      width={380}
      maxWidth={380}
      footer={footer}
    >
      {hasConflicts && (
        <div
          style={{
            padding: 8,
            marginBottom: 10,
            border: `1px solid var(--rd, ${CT.c_dc2626})`,
            borderRadius: RAD.r6,
            background: "var(--rs, rgba(220,38,38,.08))",
          }}
        >
          <div style={{ fontWeight: 600, color: `var(--rd, ${CT.c_dc2626})`, marginBottom: 4 }}>
            {t("places.reserve.conflict", { fallback: "This time slot is already booked" })}
          </div>
          <div style={{ fontSize: FS.s11, color: "var(--ts)" }}>
            {conflictPayload.conflicts.map((c: any) => {
              const holder = collaborators.find((x: any) => x.id === c.holderId);
              const who = holder ? pname(holder) : `#${c.holderId}`;
              const subj = c.subject || (c.eventId ? eventTitle(c.eventId) : null);
              return (
                <div key={c.id} style={{ marginBottom: 2 }}>
                  {slotToTime(c.startSlot)}–{slotToTime(c.endSlot)} —{" "}
                  {c.state === "booked"
                    ? t("places.state.booked", { fallback: "Booked" })
                    : t("places.state.held", { fallback: "Held" })}{" "}
                  {t("places.reserve.byWhom", { fallback: "by" })} {who}
                  {subj ? ` · ${subj}` : ""}
                  {c.notes ? ` · ${t("places.reserve.noteLabel", { fallback: "note" })}: ${c.notes}` : ""}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
        <label style={{ fontSize: FS.s10, color: "var(--tm)", display: "flex", flexDirection: "column", gap: 2 }}>
          {t("places.reserve.date", { fallback: "Date" })}
          <input className="field-input" type="date" style={{ width: "auto" }} value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label style={{ fontSize: FS.s10, color: "var(--tm)", display: "flex", flexDirection: "column", gap: 2 }}>
          {t("places.reserve.start", { fallback: "Start" })}
          <MenuSelect
            className="field-input"
            style={{ width: "auto" }}
            value={String(startSlot)}
            onChange={(v) => setStartSlot(Number(v))}
            options={slotsOptions
              .slice(0, SCHED_SLOTS)
              .map((s) => [String(s), slotToTime(s)] as const)}
          />
        </label>
        <label style={{ fontSize: FS.s10, color: "var(--tm)", display: "flex", flexDirection: "column", gap: 2 }}>
          {t("places.reserve.end", { fallback: "End" })}
          <MenuSelect
            className="field-input"
            style={{ width: "auto" }}
            value={String(endSlot)}
            onChange={(v) => setEndSlot(Number(v))}
            options={slotsOptions
              .slice(1)
              .map((s) => [String(s), slotToTime(s)] as const)}
          />
        </label>
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: FS.s10, color: "var(--tm)" }}>{t("places.reserve.state", { fallback: "State" })}</label>
        <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
          <label style={{ fontSize: FS.s12 }}>
            <input type="radio" name="resState" checked={state === "booked"} onChange={() => setState("booked")} />{" "}
            {t("places.btn.book", { fallback: "Book" })}
          </label>
          <label style={{ fontSize: FS.s12 }}>
            <input type="radio" name="resState" checked={state === "held"} onChange={() => setState("held")} />{" "}
            {t("places.btn.hold", { fallback: "Put on hold" })}
          </label>
        </div>
      </div>
      <label style={{ fontSize: FS.s10, color: "var(--tm)", display: "block", marginBottom: 10 }}>
        {t("places.reserve.subject", { fallback: "Subject" })}
        <input
          className="field-input"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder={t("places.reserve.subjectPh", { fallback: "What is this reservation for?" })}
        />
      </label>
      <label style={{ fontSize: FS.s10, color: "var(--tm)", display: "block", marginBottom: 10 }}>
        {t("places.reserve.event", { fallback: "Linked calendar event (optional)" })}
        <MenuSelect
          className="field-input"
          value={eventId != null ? String(eventId) : ""}
          onChange={(v) => setEventId(v ? Number(v) : null)}
          options={[
            [
              "",
              t("places.reserve.noEvent", { fallback: "— No linked event —" }),
            ] as const,
            /* the option's three children were adjacent text nodes */
            ...sortedEvents.map(
              (e: any) =>
                [
                  String(e.id),
                  `${fmtDay(e.date)} · ${e.title || t("places.reserve.untitledEvent", { fallback: "(untitled event)" })}`,
                ] as const,
            ),
          ]}
        />
        {/* ── BRIEF 124 §3 — THE TITLE IS A CONTROL, AND THAT IS THE WHOLE §.
            One call site, calling the callback the grid rows already call.

            A RESERVATION WITH NO `eventId` RENDERS AS IT DOES TODAY: the arm
            is absent entirely, so there is nothing to click and nothing that
            looks clickable. Same for an eventId the viewer's loaded agenda
            cannot resolve — `eventTitle` answers null and no control appears,
            which is the honest reading of "I do not have that event".

            AND A DANGLING `eventId` CANNOT HAPPEN. [MEASURED on the live
            database] `PlaceReservation_eventId_fkey` is `confdeltype = 'n'` —
            ON DELETE SET NULL — so deleting an event NULLs the column and the
            reservation falls back to the no-event arm above. Belt and braces:
            `DELETE /agenda/:id` releases the bound reservation outright
            (`releaseRoomLeg`, in server/src/routes/agenda.js) before deleting the row, so
            usual path removes the reservation rather than orphaning it.

            THE PERMISSION ANSWER IS `tryNavCase`'s TOAST, NOT A 404. D.8
            governs a SERVER read across a tenancy wall; this is a client-side
            jump inside one firm, and `_navCase` already answers it by naming
            the Responsible Partner and refusing to queue the scroll. Nothing
            about it is changed or re-decided here. */}
        {eventId && onOpenEvent && eventTitle(eventId) && (
          <button
            type="button"
            className="btn"
            data-open-linked-event={eventId}
            onClick={() => onOpenEvent(linkedEventForJump(eventId))}
            style={{
              marginTop: 6,
              fontSize: FS.s10,
              padding: "3px 8px",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            {/* The key the GRID's own jump already uses (:1004) and which is
                already seeded en/fr/nl. Same act, same words — a second key
                saying the same thing in slightly different English is how two
                controls that do one thing come to disagree. */}
            {t("places.reserve.openEvent", { fallback: "Open linked event" })} ›
          </button>
        )}
      </label>
      <label style={{ fontSize: FS.s10, color: "var(--tm)", display: "block" }}>
        {t("places.reserve.notes", { fallback: "Notes" })}
        <input className="field-input" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>
      {!isEdit && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: FS.s10, color: "var(--tm)", marginBottom: 4 }}>
            {t("places.reserve.notifyList", { fallback: "Notify collaborators" })}
          </div>
          <div
            style={{
              maxHeight: 160,
              overflowY: "auto",
              border: "1px solid var(--br)",
              borderRadius: RAD.r5,
              padding: 6,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {collaborators
              .filter((c: any) => isChoosable(c) && c.coType !== "external" && c.id !== currentUserId)
              .map((c: any) => {
                const on = notify.includes(c.id);
                return (
                  <label key={c.id} style={{ fontSize: FS.s11, display: "flex", gap: 4, alignItems: "center" }}>
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={(e) =>
                        setNotify((arr) => (e.target.checked ? [...arr, c.id] : arr.filter((x) => x !== c.id)))
                      }
                    />
                    {pname(c)}
                  </label>
                );
              })}
          </div>
        </div>
      )}
    </ModalLayout>
  );
}

// ── Per-place mini-grid ──────────────────────────────────────────────
// Each room owns its OWN 15-day window (windowStart from the parent's
// per-room map + onWindowChange) and fetches its OWN reservations for that
// window — independent of every other room and of the overview.
function PlaceGrid({
  place,
  windowStart, // Date — this room's window start
  onWindowChange, // (Date) => void — update this room's window
  collaborators,
  events, // agenda events (for hover labels)
  currentUserId,
  highlightOwn,
  searchHitIds,
  refreshSignal, // bump to force a reservation refetch after a mutation
  onPaintCommit, // ({placeId, date, startSlot, endSlot}) => void
  onResClick, // (res) => void  (open edit modal)
  onOpenEvent, // (linkedEvent) => void  (navigate to the linked agenda event)
}: any) {
  const { t, locale, fd } = useTranslation();
  const { tip, bind: tipBind, hideTip } = useHoverTip();
  const days = useMemo(
    () => Array.from({ length: WINDOW_DAYS }, (_, i) => addDays(windowStart, i)),
    [windowStart],
  );
  const windowEnd = useMemo(() => addDays(windowStart, WINDOW_DAYS - 1), [windowStart]);
  const [reservations, setReservations] = useState<any[]>([]);
  useEffect(() => {
    const from = toIsoDate(windowStart);
    const to = toIsoDate(windowEnd);
    let cancelled = false;
    api
      .get(`/reservations?from=${from}&to=${to}&placeId=${place.id}`)
      .then((r) => {
        if (!cancelled) setReservations(Array.isArray(r) ? r : []);
      })
      .catch(() => {
        if (!cancelled) setReservations([]);
      });
    return () => {
      cancelled = true;
    };
  }, [windowStart, windowEnd, place.id, refreshSignal]);
  const coById = useMemo(() => {
    const m = new Map();
    for (const c of collaborators) m.set(c.id, c);
    return m;
  }, [collaborators]);
  const eventTitleById = (id: any) => {
    const e = (events || []).find((x: any) => x.id === id);
    return e ? e.title || t("places.reserve.untitledEvent", { fallback: "(untitled event)" }) : null;
  };
  const handleDown = (dayIdx: any, slotIdx: any, e: any) => {
    e.preventDefault();
    e.stopPropagation();
    _painting = true;
    _paintDrag = {
      placeId: place.id,
      dayIdx,
      startSlot: slotIdx,
      currentSlot: slotIdx,
      mouseX: e.clientX,
      mouseY: e.clientY,
    };
    _paintCommitFn = (drag: any) => {
      const lo = Math.min(drag.startSlot, drag.currentSlot);
      const hi = Math.max(drag.startSlot, drag.currentSlot);
      onPaintCommit({
        placeId: place.id,
        date: toIsoDate(addDays(windowStart, drag.dayIdx)),
        startSlot: lo,
        endSlot: hi + 1, // exclusive end
      });
    };
    if (_rerenderFn) _rerenderFn();
  };
  const handleMove = (dayIdx: any, slotIdx: any, e: any) => {
    if (!_painting || !_paintDrag || _paintDrag.placeId !== place.id || _paintDrag.dayIdx !== dayIdx) return;
    _paintDrag.mouseX = e.clientX;
    _paintDrag.mouseY = e.clientY;
    if (slotIdx !== _paintDrag.currentSlot) {
      _paintDrag.currentSlot = slotIdx;
      if (_rerenderFn) _rerenderFn();
    }
  };

  // Group reservations by day index for fast lookup.
  const resByDay = useMemo(() => {
    const m: any[][] = Array.from({ length: WINDOW_DAYS }, () => []);
    for (const r of reservations) {
      // local-time compare
      for (let i = 0; i < WINDOW_DAYS; i++) {
        if (toIsoDate(days[i]) === r.date) {
          m[i].push(r);
          break;
        }
      }
    }
    return m;
  }, [reservations, days]);

  // Lane assignment per day — overlapping reservations get distinct lanes
  // so a pre-existing collision renders as a side-by-side clash.
  const lanesByDay = useMemo(() => resByDay.map((d) => assignLanes(d)), [resByDay]);

  // Holder initials — Phase 31 (C5): single-sourced through utils.coIni
  // (explicit Collaborator.initials wins; blank falls back to the derived
  // firstName[0]+lastName[0] — same rule as InitialsBadge). The "?" fallback
  // covers unknown holders, so a real holder never renders as "?".
  const initialsOf = (id: any) => {
    const c = coById.get(id);
    return (c && coIni(c)) || "?";
  };
  const labelOf = (r: any) => r.subject || (r.eventId ? eventTitleById(r.eventId) : "") || "";
  // Linked agenda event for a reservation. Prefer the event the API now
  // returns inline (title/date/caseId/ccId + case ref/title — resolves
  // regardless of the viewer's loaded agenda set); fall back to the
  // locally-loaded events (which don't carry the case ref/title → null).
  const linkedEventOf = (r: any) => {
    if (!r) return null;
    if (r.event) return r.event;
    if (r.eventId) {
      const e = (events || []).find((x: any) => x.id === r.eventId);
      if (e)
        return {
          id: e.id,
          title: e.title || null,
          date: e.date || null,
          caseId: e.caseId != null ? e.caseId : e.caId != null ? e.caId : null,
          ccId: e.ccId != null ? e.ccId : null,
          caseTitle: e.caseTitle || null,
          caseRef: e.caseRef || null,
          ccTitle: e.ccTitle || null,
          ccRef: e.ccRef || null,
        };
      return { id: r.eventId, title: null, date: null, caseId: null, ccId: null, caseTitle: null, caseRef: null, ccTitle: null, ccRef: null };
    }
    return null;
  };
  // The high-value at-a-glance datum for a linked case/cc event is the case
  // TITLE (shown on the block); the ref (number) goes to the tooltip.
  const caseTitleOf = (le: any) => (le ? le.caseTitle || le.ccTitle || null : null);
  const caseRefOf = (le: any) => (le ? le.caseRef || le.ccRef || null : null);
  const titleOf = (r: any) => {
    const holder = coById.get(r.holderId);
    const who = holder ? holder.initials || pname(holder) : "#" + r.holderId;
    const parts = [
      `${slotToTime(r.startSlot)}–${slotToTime(r.endSlot)}`,
      r.state === "booked"
        ? t("places.state.booked", { fallback: "Booked" })
        : t("places.state.held", { fallback: "Held" }),
      who,
    ];
    const subj = r.subject || (r.eventId ? eventTitleById(r.eventId) : null);
    if (subj) parts.push(subj);
    const le = linkedEventOf(r);
    if (le && le.title && le.title !== subj) parts.push("📅 " + le.title);
    const cTtl = caseTitleOf(le);
    const cRef = caseRefOf(le);
    if (cTtl || cRef) parts.push("⚖ " + [cRef, cTtl].filter(Boolean).join(" · "));
    if (r.notes) parts.push(t("places.reserve.noteLabel", { fallback: "note" }) + ": " + r.notes);
    return parts.join(" · ");
  };

  const baseAvail = place.baseAvailability || null;
  const isAvailable = (dayIdx: any, slotIdx: any) => {
    if (!baseAvail) return true;
    const dow = isoDow(days[dayIdx]);
    const arr = baseAvail[dow];
    if (arr === undefined) return true;
    if (!Array.isArray(arr)) return true;
    return arr.includes(slotIdx);
  };

  const today = toIsoDate(new Date());

  // Cursor-anchored drag tooltip
  const showDragTip =
    _painting && _paintDrag && _paintDrag.placeId === place.id
      ? (() => {
          const lo = Math.min(_paintDrag.startSlot, _paintDrag.currentSlot);
          const hi = Math.max(_paintDrag.startSlot, _paintDrag.currentSlot);
          return {
            text: slotToTime(lo) + "–" + slotToTime(hi + 1),
            x: _paintDrag.mouseX + 14,
            y: _paintDrag.mouseY + 10,
          };
        })()
      : null;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "120px 1fr",
        gap: 8,
        marginBottom: 14,
        padding: 8,
        border: "1px solid var(--br)",
        borderRadius: RAD.r6,
        background: "var(--bg)",
        ...(searchHitIds && !searchHitIds.has(place.id) ? { display: "none" } : {}),
      }}
    >
      {/* Place header */}
      <div style={{ paddingTop: 4 }}>
        {place.photo && (
          <img
            src={place.photo}
            alt=""
            style={{
              width: "100%",
              maxWidth: 110,
              height: 60,
              objectFit: "cover",
              borderRadius: RAD.r5,
              border: "1px solid var(--br)",
              marginBottom: 4,
              display: "block",
            }}
          />
        )}
        <div style={{ fontWeight: 700, fontSize: FS.s13, lineHeight: 1.2 }}>{place.name}</div>
        {place.locationCode && (
          <div style={{ fontSize: FS.s10, color: "var(--tm)", marginTop: 2 }}>{place.locationCode}</div>
        )}
        {place.capacity != null && (
          <div style={{ fontSize: FS.s10, color: "var(--tm)", marginTop: 2 }}>
            {t("places.capacity", { fallback: "Capacity" })}: {place.capacity}
          </div>
        )}
        {place.stateCode && (
          <div style={{ fontSize: FS.s9, color: "var(--am)", marginTop: 4, textTransform: "uppercase" }}>
            {place.stateCode}
          </div>
        )}
        {/* This room's own 15-day navigator (independent per room). */}
        <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
          {/* Buttons grouped in a no-wrap flex so the −15 / Today / +15 trio
              always stays on a single row regardless of label length (e.g. the
              longer NL "Vandaag"); the date range drops below via width:100%. */}
          <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "nowrap", flexShrink: 0 }}>
          <button
            className="btn btn-ghost"
            type="button"
            style={{ padding: "2px 6px", fontSize: FS.s10, whiteSpace: "nowrap" }}
            onClick={() => onWindowChange(addDays(windowStart, -WINDOW_DAYS))}
            title={t("places.prev", { fallback: "Previous 15 days" })}
          >
            -15
          </button>
          <button
            className="btn btn-ghost"
            type="button"
            style={{ padding: "2px 6px", fontSize: FS.s10 }}
            onClick={() => {
              const d = new Date();
              d.setHours(0, 0, 0, 0);
              onWindowChange(d);
            }}
            title={t("places.today", { fallback: "Today" })}
          >
            {t("places.today", { fallback: "Today" })}
          </button>
          <button
            className="btn btn-ghost"
            type="button"
            style={{ padding: "2px 6px", fontSize: FS.s10 }}
            onClick={() => onWindowChange(addDays(windowStart, WINDOW_DAYS))}
            title={t("places.next", { fallback: "Next 15 days" })}
          >
            +15
          </button>
          </div>
          <div style={{ fontSize: FS.s9, color: "var(--tm)", fontVariantNumeric: "tabular-nums", width: "100%", marginTop: 2 }}>
            {fd(windowStart)} → {fd(windowEnd)}
          </div>
        </div>
      </div>
      {/* Mini-grid — every cell is EXPLICITLY placed (gridColumn/gridRow) so
          the absolutely-positioned reservation overlay containers below can
          share the same grid areas without bumping the background cells out
          of auto-flow (that bump was the under-layer regression). */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `48px repeat(${WINDOW_DAYS}, minmax(0, 1fr))`,
          gridTemplateRows: `auto auto repeat(${SCHED_SLOTS}, 14px)`,
          gap: 0,
          background: "var(--bg)",
          border: "1px solid var(--br)",
          fontSize: FS.s9,
          userSelect: "none",
        }}
      >
        {/* Month band: corner + per-day labels (shown where the month changes) */}
        <div style={{ gridColumn: 1, gridRow: 1, background: "var(--bg)" }} />
        {days.map((d, i) => {
          const showMonth = i === 0 || d.getMonth() !== days[i - 1].getMonth();
          return (
            <div
              key={"mh-" + i}
              style={{
                gridColumn: i + 2,
                gridRow: 1,
                background: "var(--bg)",
                color: "var(--tm)",
                fontSize: FS.s8,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.3,
                textAlign: "left",
                paddingLeft: 2,
                borderLeft: showMonth ? "1px solid var(--br)" : "none",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              {showMonth ? d.toLocaleDateString(locale, { month: "short" }) : ""}
            </div>
          );
        })}
        {/* Day headers: corner + weekday/day (localized) */}
        <div style={{ gridColumn: 1, gridRow: 2, background: "var(--bg)", borderBottom: "1px solid var(--br)" }} />
        {days.map((d, i) => {
          const iso = toIsoDate(d);
          const isToday = iso === today;
          return (
            <div
              key={"dh-" + i}
              style={{
                gridColumn: i + 2,
                gridRow: 2,
                background: isToday ? "var(--bs, var(--bg))" : "var(--bg)",
                color: isToday ? "var(--pp)" : "var(--ts)",
                fontWeight: isToday ? 700 : 500,
                textAlign: "center",
                padding: "2px 0",
                borderBottom: "1px solid var(--br)",
                borderLeft: "1px solid rgba(128,128,128,0.10)",
                fontSize: FS.s9,
                fontVariantNumeric: "tabular-nums",
              }}
              title={iso}
            >
              {d.toLocaleDateString(locale, { weekday: "short" }).slice(0, 2)}
              <br />
              {d.getDate()}
            </div>
          );
        })}
        {/* Time-axis labels — left column, one per hour, explicitly placed */}
        {Array.from({ length: SCHED_SLOTS }, (_, slotIdx) => {
          const isHour = slotIdx % 2 === 0;
          return (
            <div
              key={"tl-" + slotIdx}
              style={{
                gridColumn: 1,
                gridRow: slotIdx + 3,
                background: "var(--bg)",
                color: "var(--tm)",
                fontSize: FS.s8,
                textAlign: "right",
                paddingRight: 4,
                lineHeight: "14px",
                borderBottom: isHour ? "1px solid var(--br)" : "none",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {isHour ? `${SCHED_START + slotIdx / 2}:00` : ""}
            </div>
          );
        })}
        {/* Background day cells — gridlines on every day (incl. reserved ones).
            Schedule-style availability: the AVAILABLE band gets a clean subtle
            solid fill (var(--as), the theme accent-surface — the low-alpha
            counterpart of Schedule's onsite fill); out-of-hours stays plain. */}
        {days.map((d, dayIdx) =>
          Array.from({ length: SCHED_SLOTS }, (_, slotIdx) => {
            const isHour = slotIdx % 2 === 0;
            const avail = isAvailable(dayIdx, slotIdx);
            const dragOn =
              _painting && _paintDrag && _paintDrag.placeId === place.id && _paintDrag.dayIdx === dayIdx
                ? slotIdx >= Math.min(_paintDrag.startSlot, _paintDrag.currentSlot) &&
                  slotIdx <= Math.max(_paintDrag.startSlot, _paintDrag.currentSlot)
                : false;
            return (
              <div
                key={"c-" + dayIdx + "-" + slotIdx}
                onMouseDown={(e) => handleDown(dayIdx, slotIdx, e)}
                onMouseMove={(e) => handleMove(dayIdx, slotIdx, e)}
                style={{
                  gridColumn: dayIdx + 2,
                  gridRow: slotIdx + 3,
                  background: dragOn ? `var(--gn, ${CT.c_34d399})` : avail ? "var(--as)" : "transparent",
                  borderBottom: isHour ? "1px solid var(--br)" : "1px solid rgba(128,128,128,0.12)",
                  borderLeft: "1px solid rgba(128,128,128,0.10)",
                  cursor: "crosshair",
                }}
                title={`${slotToTime(slotIdx)} (${avail ? t("places.available", { fallback: "available" }) : t("places.unavailable", { fallback: "unavailable" })})`}
              />
            );
          }),
        )}
        {/* Reservation overlay layer: one positioned container per day; each
            reservation is a rounded block, lane-split so collisions show as
            a side-by-side clash rather than stacking/hiding. */}
        {days.map((d, dayIdx) => {
          const { laneCount, items } = lanesByDay[dayIdx];
          if (!items.length) return null;
          return (
            <div
              key={"ov-" + dayIdx}
              style={{
                gridColumn: dayIdx + 2,
                gridRow: `3 / span ${SCHED_SLOTS}`,
                position: "relative",
                pointerEvents: "none",
              }}
            >
              {items.map(({ res, lane, clash }) => {
                const top = (res.startSlot / SCHED_SLOTS) * 100;
                const height = ((res.endSlot - res.startSlot) / SCHED_SLOTS) * 100;
                const left = (lane / laneCount) * 100;
                const width = 100 / laneCount;
                const owned = res.holderId === currentUserId;
                const blockBg = res.state === "booked" ? `var(--pp, ${CT.c_4a7cff})` : `var(--am, ${CT.c_fbbf24})`;
                const label = labelOf(res);
                const le = linkedEventOf(res);
                const evTitle = le && le.title ? le.title : null;
                const caseTtl = caseTitleOf(le);
                const caseRef = caseRefOf(le);
                return (
                  <div
                    key={res.id}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      hideTip();
                      onResClick(res);
                    }}
                    {...tipBind(titleOf(res))}
                    style={{
                      position: "absolute",
                      top: `calc(${top}% + 1px)`,
                      height: `calc(${height}% - 2px)`,
                      left: `calc(${left}% + 1px)`,
                      width: `calc(${width}% - 2px)`,
                      background: blockBg,
                      borderRadius: RAD.r4,
                      border: clash ? `2px solid var(--rd, ${CT.c_dc2626})` : "1px solid rgba(0,0,0,0.25)",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.35)",
                      outline: highlightOwn && owned ? `2px solid var(--gn, ${CT.c_34d399})` : "none",
                      outlineOffset: -2,
                      cursor: "pointer",
                      pointerEvents: "auto",
                      overflow: "hidden",
                      padding: "1px 3px",
                      boxSizing: "border-box",
                      color: CT.c_fff,
                      fontSize: FS.s8,
                      lineHeight: 1.1,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        fontWeight: 700,
                        textShadow: "0 0 2px rgba(0,0,0,.4)",
                      }}
                    >
                      <span>{initialsOf(res.holderId)}</span>
                      {le && (
                        <span
                          role="button"
                          title={
                            t("places.reserve.openEvent", { fallback: "Open linked event" }) +
                            (evTitle ? ": " + evTitle : "")
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onOpenEvent && onOpenEvent(le);
                          }}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            cursor: "pointer",
                            pointerEvents: "auto",
                          }}
                        >
                          <Calendar size={9} />
                        </span>
                      )}
                    </div>
                    {label && height > 5 && (
                      <div
                        style={{
                          opacity: 0.92,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          textShadow: "0 0 2px rgba(0,0,0,.4)",
                        }}
                      >
                        {label}
                      </div>
                    )}
                    {le && caseTtl && height > 9 && (
                      <div
                        role="button"
                        title={
                          t("places.reserve.openCase", { fallback: "Open case" }) +
                          ": " +
                          caseTtl +
                          (caseRef ? " · " + caseRef : "") +
                          (evTitle ? " · " + evTitle : "")
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onOpenEvent && onOpenEvent(le);
                        }}
                        style={{
                          opacity: 0.92,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          textDecoration: "underline",
                          cursor: "pointer",
                          pointerEvents: "auto",
                          textShadow: "0 0 2px rgba(0,0,0,.4)",
                        }}
                      >
                        {caseTtl}
                      </div>
                    )}
                    {clash && (
                      <span style={{ position: "absolute", top: 0, right: 2, fontSize: FS.s8, fontWeight: 700 }}>⚠</span>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      {showDragTip && (
        <div
          style={{
            position: "fixed",
            left: showDragTip.x,
            top: showDragTip.y,
            background: "var(--bg)",
            border: "1px solid var(--br)",
            borderRadius: RAD.r4,
            padding: "2px 6px",
            fontSize: FS.s10,
            color: "var(--ts)",
            pointerEvents: "none",
            zIndex: Z.pane,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {showDragTip.text}
        </div>
      )}
      <HoverTip tip={tip} />
    </div>
  );
}

// ── Derived firm-wide activity feed (C5) ─────────────────────────────
// CV-timeline idiom: derive at render from two sources merged
// chronologically — active reservations (the bookings themselves) and
// place.* notifications (cross-cutting events: bumps, reserve-notifies).
// No persisted activity-log table; we read the same /api/reservations
// window already fetched for the grid AND /api/notifications/feed
// type-filtered to place.* across all recipients (the firm-wide read
// the (type, createdAt DESC) index serves).
function PlacesActivityFeed({ reservations, places, collaborators }: any) {
  const { t } = useTranslation();
  const [feed, setFeed] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchFeed = useCallback(async () => {
    try {
      const rows = await api.get("/notifications/feed?type=place.bump,place.reserve_notify,place.edit");
      setFeed(Array.isArray(rows) ? rows : []);
      setLoaded(true);
    } catch {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    fetchFeed();
    const id = setInterval(fetchFeed, 60000);
    return () => clearInterval(id);
  }, [open, fetchFeed]);

  const placeOf = (id: any) => places.find((p: any) => p.id === id);
  const coOf = (id: any) => collaborators.find((c: any) => c.id === id);

  const items = useMemo(() => {
    const out = [];
    // Reservations (within the current grid window) — chronological by
    // createdAt; firm-wide visibility per scope §9.
    for (const r of reservations) {
      const place = placeOf(r.placeId);
      const holder = coOf(r.holderId);
      out.push({
        key: "r-" + r.id,
        ts: r.createdAt || (r.date + "T00:00:00Z"),
        type: "reservation",
        msg: `${holder ? pname(holder) : t("places.feed.someone", { fallback: "Someone" })} ${
          r.state === "booked"
            ? t("places.feed.booked", { fallback: "booked" })
            : t("places.feed.held", { fallback: "held" })
        } ${place ? place.name : t("places.feed.placeNum", { fallback: "place #" }) + r.placeId} · ${r.date} ${slotToTime(r.startSlot)}–${slotToTime(r.endSlot)}`,
      });
    }
    // Persisted notifications (place.*).
    for (const n of feed) {
      const p = n.payload || {};
      const place = placeOf(p.placeId);
      const actor = coOf(p.byCollaboratorId);
      if (n.type === "place.bump") {
        out.push({
          key: "n-" + n.id,
          ts: n.createdAt,
          type: "bump",
          msg: `${actor ? pname(actor) : t("places.feed.someone", { fallback: "Someone" })} ${t("places.feed.bumped", {
            fallback: "overrode a reservation on",
          })} ${place ? place.name : t("places.feed.placeNum", { fallback: "place #" }) + p.placeId} · ${p.date} ${slotToTime(p.startSlot)}–${slotToTime(p.endSlot)}`,
        });
      } else if (n.type === "place.reserve_notify") {
        out.push({
          key: "n-" + n.id,
          ts: n.createdAt,
          type: "reserve_notify",
          msg: `${actor ? pname(actor) : t("places.feed.someone", { fallback: "Someone" })} ${t("places.feed.notified", {
            fallback: "notified about a reservation on",
          })} ${place ? place.name : t("places.feed.placeNum", { fallback: "place #" }) + p.placeId} · ${p.date}`,
        });
      } else if (n.type === "place.edit") {
        const prevRange =
          p.prevStartSlot != null ? `${slotToTime(p.prevStartSlot)}–${slotToTime(p.prevEndSlot)}` : "";
        const newRange =
          p.startSlot != null ? `${slotToTime(p.startSlot)}–${slotToTime(p.endSlot)}` : "";
        out.push({
          key: "n-" + n.id,
          ts: n.createdAt,
          type: "edit",
          msg: `${actor ? pname(actor) : t("places.feed.someone", { fallback: "Someone" })} ${t("places.feed.edited", {
            fallback: "changed a reservation on",
          })} ${place ? place.name : t("places.feed.placeNum", { fallback: "place #" }) + p.placeId} · ${p.date}${
            prevRange && newRange ? ` (${prevRange} → ${newRange})` : ""
          }`,
        });
      }
    }
    out.sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));
    return out.slice(0, 80);
  }, [reservations, feed, places, collaborators, t]);

  return (
    <div
      style={{
        marginTop: 14,
        border: "1px solid var(--br)",
        borderRadius: RAD.r6,
        background: "var(--bg)",
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          padding: "8px 10px",
          textAlign: "left",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          fontWeight: 600,
          fontSize: FS.s12,
          color: "var(--ts)",
        }}
      >
        {open ? "▼" : "▶"} {t("places.feed.title", { fallback: "Activity (firm-wide)" })}{" "}
        <span style={{ color: "var(--tm)", fontWeight: 400 }}>
          {open ? `(${items.length})` : ""}
        </span>
      </button>
      {open && (
        <div style={{ padding: "0 10px 10px", maxHeight: 240, overflowY: "auto" }}>
          {!loaded ? (
            <div style={{ fontSize: FS.s11, color: "var(--tm)" }}>{t("common.loading", { fallback: "Loading…" })}</div>
          ) : items.length === 0 ? (
            <div style={{ fontSize: FS.s11, color: "var(--tm)" }}>
              {t("places.feed.empty", { fallback: "No activity yet." })}
            </div>
          ) : (
            items.map((it) => (
              <div
                key={it.key}
                style={{
                  fontSize: FS.s11,
                  color: it.type === "bump" || it.type === "edit" ? "var(--am)" : "var(--ts)",
                  padding: "3px 0",
                  borderBottom: "1px dotted var(--br)",
                  display: "flex",
                  gap: 8,
                }}
              >
                <span style={{ color: "var(--tm)", flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
                  {(it.ts || "").slice(0, 16).replace("T", " ")}
                </span>
                <span>{it.msg}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Collective overview (day-major, cross-place at-a-glance) ─────────
// Mirrors the Schedule section's column layout: columns are DAYS; inside
// each day every place is a thin side-by-side sub-column; hours run down.
// 8 working-day window with its own navigation (separate from the per-place
// 15-day window). Reservations, collision clashes, and base-availability
// dimming all render; clicking a place code jumps to its detail grid below;
// click a free slot to reserve, a reserved slot to edit.
function CollectiveOverview({
  schedAxis,
  places,
  categoryLabel,
  collaborators,
  currentUserId,
  refreshSignal,
  onReserveAt,
  onResClick,
  onJumpToPlace,
}: any) {
  const { t, locale, fd } = useTranslation();
  const { tip, bind: tipBind, hideTip } = useHoverTip();
  const [ovStart, setOvStart] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [ovRes, setOvRes] = useState<any[]>([]);
  // Paint-to-book: a tick to re-render the drag highlight as the cursor moves.
  const [, setOvPaintTick] = useState(0);
  const ovRerender = useCallback(() => setOvPaintTick((n) => n + 1), []);
  _ovRerenderFn = ovRerender;
  // Calendar days (weekends INCLUDED) so weekend reservations show and week
  // boundaries are visible; weekend columns render at half width.
  const days = useMemo(() => calendarDays(ovStart, OVERVIEW_DAYS), [ovStart]);
  const activePlaces = useMemo(() => places.filter((p: any) => !p.archived), [places]);

  // Commit a painted range (or a single click) → open the reserve modal.
  useEffect(() => {
    window.addEventListener("mouseup", handleOvMouseUp, true);
    return () => window.removeEventListener("mouseup", handleOvMouseUp, true);
  }, []);

  useEffect(() => {
    if (!days.length) return;
    const from = toIsoDate(days[0]);
    const to = toIsoDate(days[days.length - 1]);
    let cancelled = false;
    api
      .get(`/reservations?from=${from}&to=${to}`)
      .then((r) => {
        if (!cancelled) setOvRes(Array.isArray(r) ? r : []);
      })
      .catch(() => {
        if (!cancelled) setOvRes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [days, refreshSignal]);

  const coById = useMemo(() => {
    const m = new Map();
    for (const c of collaborators) m.set(c.id, c);
    return m;
  }, [collaborators]);

  const resByPlaceDate = useMemo(() => {
    const m = new Map();
    for (const r of ovRes) {
      const k = r.placeId + "|" + r.date;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    }
    return m;
  }, [ovRes]);

  const isAvail = (place: any, day: any, slotIdx: any) => {
    const ba = place.baseAvailability;
    if (!ba) return true;
    const arr = ba[isoDow(day)];
    if (arr === undefined || !Array.isArray(arr)) return true;
    return arr.includes(slotIdx);
  };

  // Holder initials — C63 single source (stored initials win, name-derived
  // fallback), via the shared utils.coIni.
  const initialsOf = (id: any) => {
    const c = coById.get(id);
    return c ? coIni(c) : "";
  };

  const today = toIsoDate(new Date());

  if (!activePlaces.length) return null;

  const NP = activePlaces.length;
  const dayMin = Math.max(NP * 18, 80); // a FULL column's min width (width 2)
  // 58 §5b — per-weekday column weight from the firm's axis. `width` is a
  // half-column unit, so today's hand-drawn "weekend columns are half width"
  // is the default (2 for Mon–Fri, 1 for Sat/Sun) and reproduces exactly.
  const _colW = (d: Date) => (axisDayOf(schedAxis, d.getDay()).width || 1) / 2;
  const colTemplate =
    "40px " +
    days
      .map((d) => `minmax(${Math.round(dayMin * _colW(d))}px, ${_colW(d)}fr)`)
      .join(" ");
  const minW = 40 + days.reduce((a, d) => a + Math.round(dayMin * _colW(d)), 0);

  const navBtn = (label: any, onClick: any) => (
    <button className="btn btn-ghost" type="button" onClick={onClick}>
      {label}
    </button>
  );

  return (
    <div style={{ marginBottom: 22, border: "1px solid var(--br)", borderRadius: RAD.r6, background: "var(--bg)", padding: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        <h3 style={{ fontSize: FS.s13, margin: 0, color: "var(--ts)" }}>
          {t("places.overview.titleCat", { fallback: "Availability overview" })}
          {categoryLabel ? <span style={{ color: "var(--tm)", fontWeight: 400 }}> — {categoryLabel}</span> : null}
        </h3>
        <div style={{ display: "flex", gap: 6, marginLeft: "auto", alignItems: "center" }}>
          {navBtn("-8", () => setOvStart(addDays(ovStart, -OVERVIEW_DAYS)))}
          <span style={{ fontSize: FS.s11, color: "var(--tm)", fontVariantNumeric: "tabular-nums" }}>
            {fd(days[0])} → {fd(days[days.length - 1])}
          </span>
          {navBtn("+8", () => setOvStart(addDays(ovStart, OVERVIEW_DAYS)))}
          {navBtn(t("places.today", { fallback: "Today" }), () => {
            const d = new Date();
            d.setHours(0, 0, 0, 0);
            setOvStart(d);
          })}
        </div>
      </div>
      <div style={{ overflowX: "auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: colTemplate,
            gridTemplateRows: `auto auto repeat(${SCHED_SLOTS}, 14px)`,
            minWidth: minW,
            fontSize: FS.s9,
            userSelect: "none",
          }}
        >
          {/* Corner spans both header rows */}
          <div style={{ gridColumn: 1, gridRow: "1 / span 2", background: "var(--bg)" }} />
          {/* Row 1: day headers (weekend tint + week-boundary rule on Monday) */}
          {days.map((d, di) => {
            const iso = toIsoDate(d);
            const we = isNonWorkingDow(schedAxis, d.getDay());
            const weekStart = d.getDay() === 1; // Monday
            return (
              <div
                key={"dh-" + di}
                style={{
                  gridColumn: di + 2,
                  gridRow: 1,
                  textAlign: "center",
                  fontSize: FS.s9,
                  fontWeight: 600,
                  padding: "3px 2px",
                  color: iso === today ? "var(--pp)" : we ? "var(--tm)" : "var(--ts)",
                  background: we ? "color-mix(in srgb, var(--tp) 3%, transparent)" : "transparent",
                  borderBottom: "1px solid var(--br)",
                  borderLeft: weekStart ? "2px solid var(--br)" : "1px solid rgba(128,128,128,0.12)",
                  fontVariantNumeric: "tabular-nums",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              >
                {d.toLocaleDateString(locale, { weekday: "short" })} {d.getDate()}/{d.getMonth() + 1}
              </div>
            );
          })}
          {/* Row 2: per-day place-name strip (full names, vertical, clickable →
              jump to detail). One category only, so names fit as thin columns. */}
          {days.map((d, di) => {
            const we = isNonWorkingDow(schedAxis, d.getDay());
            const weekStart = d.getDay() === 1;
            return (
              <div
                key={"ph-" + di}
                style={{
                  gridColumn: di + 2,
                  gridRow: 2,
                  display: "flex",
                  background: we ? "color-mix(in srgb, var(--tp) 3%, transparent)" : "transparent",
                  borderBottom: "1px solid var(--br)",
                  borderLeft: weekStart ? "2px solid var(--br)" : "1px solid rgba(128,128,128,0.12)",
                }}
              >
                {activePlaces.map((p: any) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => onJumpToPlace(p.id, ovStart)}
                    title={`${p.name} — ${t("places.overview.jump", { fallback: "go to detail grid" })}`}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      // Horizontal, wrapping to up to two lines so distinct rooms
                      // ("Boardroom Aurelius" vs "Boardroom Brutus") stay legible
                      // instead of truncating to the same prefix.
                      fontSize: FS.s8,
                      fontWeight: 600,
                      lineHeight: 1.1,
                      padding: "2px 1px",
                      background: "none",
                      border: "none",
                      borderLeft: "1px solid rgba(128,128,128,0.08)",
                      color: "var(--ts)",
                      cursor: "pointer",
                      textAlign: "center",
                      whiteSpace: "normal",
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      wordBreak: "break-word",
                    }}
                  >
                    {we ? abbrevName(p.name) : p.name}
                  </button>
                ))}
              </div>
            );
          })}
          {/* Time-axis labels */}
          {Array.from({ length: SCHED_SLOTS }, (_, s) => {
            const isHour = s % 2 === 0;
            return (
              <div
                key={"tl-" + s}
                style={{
                  gridColumn: 1,
                  gridRow: s + 3,
                  fontSize: FS.s7,
                  color: "var(--tm)",
                  textAlign: "right",
                  paddingRight: 3,
                  lineHeight: "14px",
                  borderBottom: isHour ? "1px solid var(--br)" : "none",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {isHour ? `${SCHED_START + s / 2}:00` : ""}
              </div>
            );
          })}
          {/* Day cells: each (day, slot) is a flex row of per-place sub-cells */}
          {days.map((d, di) => {
            const iso = toIsoDate(d);
            const we = isNonWorkingDow(schedAxis, d.getDay());
            const weekStart = d.getDay() === 1;
            return Array.from({ length: SCHED_SLOTS }, (_, s) => {
              const isHour = s % 2 === 0;
              return (
                <div
                  key={"c-" + di + "-" + s}
                  style={{
                    gridColumn: di + 2,
                    gridRow: s + 3,
                    display: "flex",
                    background: we ? "color-mix(in srgb, var(--tp) 2.5%, transparent)" : "transparent",
                    borderBottom: isHour ? "1px solid var(--br)" : "1px solid rgba(128,128,128,0.10)",
                    borderLeft: weekStart ? "2px solid var(--br)" : "1px solid rgba(128,128,128,0.12)",
                  }}
                >
                  {activePlaces.map((p: any) => {
                    const list = resByPlaceDate.get(p.id + "|" + iso) || [];
                    const covering = list.filter((r: any) => s >= r.startSlot && s < r.endSlot);
                    const res = covering[0];
                    const clash = covering.length > 1;
                    const avail = isAvail(p, d, s);
                    // Schedule-style: reservation = solid state colour; free &
                    // available = subtle accent-surface band (var(--as)); free &
                    // out-of-hours = plain. No hatch.
                    let bgColor = "transparent";
                    if (res) {
                      bgColor = clash
                        ? `var(--rd, ${CT.c_dc2626})`
                        : res.state === "booked"
                          ? `var(--pp, ${CT.c_4a7cff})`
                          : `var(--am, ${CT.c_fbbf24})`;
                    } else if (avail) {
                      bgColor = "var(--as)";
                    }
                    const owned = res && res.holderId === currentUserId;
                    const holder = res ? coById.get(res.holderId) : null;
                    const who = holder ? holder.initials || pname(holder) : res ? "#" + res.holderId : "";
                    // Round the run ends + nick a 1px gap at start/end so a
                    // multi-slot reservation reads as one rounded block and
                    // successive ones don't merge into a single bar.
                    const runFirst = res && s === res.startSlot;
                    const runLast = res && s === res.endSlot - 1;
                    const runBr = res
                      ? runFirst && runLast
                        ? 3
                        : runFirst
                          ? "3px 3px 0 0"
                          : runLast
                            ? "0 0 3px 3px"
                            : 0
                      : 0;
                    // Paint-to-book: free + available cells start/extend a drag
                    // within this (place, day) column; commit opens reserve.
                    const paintable = !res && avail;
                    const dragOn =
                      _ovPainting &&
                      _ovDrag &&
                      _ovDrag.placeId === p.id &&
                      _ovDrag.iso === iso &&
                      s >= Math.min(_ovDrag.startSlot, _ovDrag.currentSlot) &&
                      s <= Math.max(_ovDrag.startSlot, _ovDrag.currentSlot);
                    return (
                      <div
                        key={p.id}
                        onMouseDown={
                          paintable
                            ? (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                _ovPainting = true;
                                _ovDrag = { placeId: p.id, iso, startSlot: s, currentSlot: s };
                                _ovCommitFn = (drag: any) => {
                                  const lo = Math.min(drag.startSlot, drag.currentSlot);
                                  const hi = Math.max(drag.startSlot, drag.currentSlot);
                                  onReserveAt(drag.placeId, drag.iso, lo, hi + 1);
                                };
                                ovRerender();
                              }
                            : undefined
                        }
                        onMouseMove={(e) => {
                          if (!_ovPainting || !_ovDrag || _ovDrag.placeId !== p.id || _ovDrag.iso !== iso) return;
                          if (s !== _ovDrag.currentSlot) {
                            _ovDrag.currentSlot = s;
                            ovRerender();
                          }
                        }}
                        onClick={res ? (e) => { e.stopPropagation(); hideTip(); onResClick(res); } : undefined}
                        {...tipBind(
                          res
                            ? `${p.name} · ${slotToTime(res.startSlot)}–${slotToTime(res.endSlot)} · ${
                                res.state === "booked"
                                  ? t("places.state.booked", { fallback: "Booked" })
                                  : t("places.state.held", { fallback: "Held" })
                              } · ${who}${res.subject ? " · " + res.subject : ""}${res.event && res.event.title ? " · 📅 " + res.event.title : ""}${
                                res.event && (res.event.caseRef || res.event.ccRef)
                                  ? " · ⚖ " + (res.event.caseRef || res.event.ccRef)
                                  : ""
                              }${res.notes ? " · " + t("places.reserve.noteLabel", { fallback: "note" }) + ": " + res.notes : ""}${clash ? " · ⚠ " + t("places.overview.clash", { fallback: "CLASH" }) : ""}`
                            : `${p.name} · ${slotToTime(s)} · ${
                                avail
                                  ? t("places.available", { fallback: "available" })
                                  : t("places.unavailable", { fallback: "unavailable" })
                              }`,
                        )}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                          backgroundColor: dragOn ? `var(--gn, ${CT.c_34d399})` : bgColor,
                          backgroundClip: "padding-box",
                          borderLeft: "1px solid rgba(128,128,128,0.08)",
                          borderRadius: runBr,
                          marginTop: runFirst ? 1 : 0,
                          marginBottom: runLast ? 1 : 0,
                          cursor: res ? "pointer" : avail ? "crosshair" : "default",
                          outline: owned ? `1px solid var(--gn, ${CT.c_34d399})` : "none",
                          outlineOffset: -1,
                        }}
                      >
                        {/* Holder initials on the run's first slot so the
                            overview answers "who booked it" at a glance.
                            Single line, 7px, clipped to the thin cell. */}
                        {res && runFirst && initialsOf(res.holderId) && (
                          <span
                            style={{
                              fontSize: FS.s7,
                              fontWeight: 700,
                              lineHeight: 1,
                              color: CT.c_fff,
                              textShadow: "0 0 2px rgba(0,0,0,.55)",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "clip",
                              pointerEvents: "none",
                            }}
                          >
                            {initialsOf(res.holderId)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            });
          })}
        </div>
      </div>
      <HoverTip tip={tip} />
    </div>
  );
}

// ── PlacesView (top-level lateral view) ──────────────────────────────
export default function PlacesView({ co, ag, currentUser, toast, viewerCanOverride, nav, tryNavCase, schedAxis }: any) {
  const { t } = useTranslation();
  const askConfirm = useConfirm();
  /* Phase 30 Stage-3 (C-1) — reservations link agenda events on ANY case
     (meeting rooms are firm-visible); the bare nav() dead-jumped off-scope
     viewers onto a blank CaseView. C85 guard instead. */
  const _navCase = tryNavCase || ((kind: any, id: any) => (nav(kind, id), true));
  const [tick, setTick] = useState(0);
  const rerender = useCallback(() => setTick((t) => t + 1), []);
  _rerenderFn = rerender;

  // Feed/highlight window — a fixed "current" 15-day span (today..+14) used
  // ONLY to source the activity feed and the showOnlyMine list filter. The
  // per-room detail grids each own their window now (roomWindows below), and
  // the collective overview owns its own; this shared span no longer drives
  // any navigator.
  const windowStart = useMemo(() => {
    const t0 = new Date();
    t0.setHours(0, 0, 0, 0);
    return t0;
  }, []);
  const windowEnd = useMemo(() => addDays(windowStart, WINDOW_DAYS - 1), [windowStart]);

  // Per-room 15-day windows: placeId -> Date (start). Absent = today. Each
  // room's navigator and jump-alignment write here.
  const [roomWindows, setRoomWindows] = useState<Record<string, any>>({});
  const todayStart = windowStart;
  const roomStart = (id: any) => roomWindows[id] || todayStart;
  const setRoomStart = useCallback((id: any, date: any) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    setRoomWindows((m) => ({ ...m, [id]: d }));
  }, []);

  // Default state mode for next reserve (Book vs Hold). The "My" toggle
  // is a highlight, not a default-state.
  const [defaultState, setDefaultState] = useState("booked");
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  const [q, setQ] = useState("");
  // Category filter — never mix categories across the overview + detail grids.
  const [selectedCat, setSelectedCat] = useState<any>(null);

  // Data
  const [places, setPlaces] = useState<any[]>([]);
  const [reservations, setReservations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [modal, setModal] = useState<any>(null); // { kind:"new"|"edit", initial, conflictPayload? }
  // Bumped after every reservation mutation so the collective overview (which
  // fetches its own 8-day window) refetches.
  const [mutCount, setMutCount] = useState(0);
  const bumpMut = useCallback(() => setMutCount((n) => n + 1), []);

  // Reserve a range (single click → 1 slot, or a painted drag range) on a
  // given place/date. Used by both the overview and the per-place grids.
  const onReserveAt = (placeId: any, date: any, startSlot: any, endSlot: any) => {
    setModal({
      kind: "new",
      initial: { placeId, date, startSlot, endSlot: endSlot ?? startSlot + 1 },
    });
  };
  // Paint-commit from a per-place detail grid → reserve the dragged range.
  const onPaintCommit = ({ placeId, date, startSlot, endSlot }: any) => {
    setModal({ kind: "new", initial: { placeId, date, startSlot, endSlot } });
  };
  // Jump from the overview to a place's detail grid. The overview navigates
  // its own window, so align THAT room's own 15-day window to the overview's
  // start before scrolling — otherwise the room stays at its own date range
  // and lands empty for the dates the user was looking at.
  const onJumpToPlace = (placeId: any, fromStart: any) => {
    if (fromStart) setRoomStart(placeId, fromStart);
    // Defer the scroll so the (possibly re-windowed) grid has laid out.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const el = document.getElementById("place-detail-" + placeId);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }),
    );
  };

  const { entries: catEntries } = useTaxonomy("place_category");

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        api.get("/places"),
        api.get(`/reservations?from=${toIsoDate(windowStart)}&to=${toIsoDate(windowEnd)}`),
      ]);
      setPlaces(Array.isArray(p) ? p : []);
      setReservations(Array.isArray(r) ? r : []);
    } catch (e) {
      toast && toast(t("places.load_failed", { fallback: "Failed to load places" }), "error");
    } finally {
      setLoading(false);
    }
  }, [windowStart, windowEnd, toast, t]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Phase 30 (C) — live convergence: reservation/place events from the SSE
  // bridge re-pull the grid (events are lateral.places-gated server-side).
  useEffect(() => {
    let tmr: any = null;
    const h = (e: any) => {
      const ty = e?.detail?.type;
      if (ty !== "reservation" && ty !== "place") return;
      clearTimeout(tmr);
      tmr = setTimeout(loadAll, 400);
    };
    window.addEventListener("lx:sse", h);
    return () => {
      clearTimeout(tmr);
      window.removeEventListener("lx:sse", h);
    };
  }, [loadAll]);

  // Capture-phase window mouseup for paint commit.
  useEffect(() => {
    window.addEventListener("mouseup", handleMouseUp, true);
    return () => window.removeEventListener("mouseup", handleMouseUp, true);
  }, []);

  const grouped = useMemo(() => {
    // Group places by category, ordered by the taxonomy order then by place.order/name.
    const byCat = new Map();
    for (const p of places.filter((x) => !x.archived)) {
      if (!byCat.has(p.categoryCode)) byCat.set(p.categoryCode, []);
      byCat.get(p.categoryCode).push(p);
    }
    const orderedCats = catEntries
      .map((c: any) => c.code)
      .concat(Array.from(byCat.keys()).filter((c) => !catEntries.some((x: any) => x.code === c)));
    return orderedCats
      .filter((c: any) => byCat.has(c))
      .map((c: any) => [c, byCat.get(c).sort((a: any, b: any) => (a.order - b.order) || a.name.localeCompare(b.name))]);
  }, [places, catEntries]);

  const labelFor = (code: any) => {
    const e = catEntries.find((x: any) => x.code === code);
    return e ? e.labelEn || e.label || e.code : code;
  };

  // Default the category filter to the first category that has places, and
  // keep it valid if the selected one disappears.
  useEffect(() => {
    if (!grouped.length) return;
    if (!selectedCat || !grouped.some(([c]: any) => c === selectedCat)) {
      setSelectedCat(grouped[0][0]);
    }
  }, [grouped, selectedCat]);

  // Item-5 fix — resolve the rendered category DURING render. selectedCat starts
  // null and is only set by the effect above (which runs AFTER the first
  // post-load render), so without this the overview AND its detail grid were
  // absent for one render — the "Availability overview" collapse-and-return that
  // jumped "Meeting rooms" up. effectiveCat = the user's selection if still
  // valid, else the first category with places; so the very first post-load
  // paint already has a valid category and nothing collapses. The effect still
  // persists the resolved value into selectedCat (user clicks set it directly).
  const effectiveCat =
    selectedCat && grouped.some(([c]: any) => c === selectedCat)
      ? selectedCat
      : grouped[0]?.[0] ?? null;

  // Places in the selected category only (overview is single-category).
  const selectedPlaces = useMemo(
    () => (grouped.find(([c]: any) => c === effectiveCat) || [null, []])[1],
    [grouped, effectiveCat],
  );

  // Search: filter by name / location / capacity / category label.
  const searchHitIds = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return null;
    const ids = new Set();
    for (const p of places) {
      const cat = labelFor(p.categoryCode);
      const hay = `${p.name} ${p.locationCode || ""} ${p.stateCode || ""} ${cat} ${p.capacity || ""}`.toLowerCase();
      if (hay.includes(qq)) ids.add(p.id);
    }
    return ids;
  }, [q, places, catEntries]);

  // Open a reservation's linked agenda event using the app's EXISTING
  // single-event mechanism (same as AgendaView row-click): queue the
  // scroll-to-highlight, then navigate to the owning case / collective
  // case. Firm-wide events (no case) fall back to the global agenda.
  const openLinkedEvent = (le: any) => {
    if (!le) return;
    // C-1 — scroll queues only when navigation actually runs (C85 contract).
    if (le.caseId != null) {
      if (_navCase("ca", le.caseId) && le.id) queueScrollToEv(le.id);
    } else if (le.ccId != null) {
      if (_navCase("cc", le.ccId) && le.id) queueScrollToEv(le.id);
    } else {
      if (le.id) queueScrollToEv(le.id);
      nav && nav("ag");
    }
  };
  const onResClick = (res: any) => {
    setModal({
      kind: "edit",
      initial: {
        id: res.id,
        placeId: res.placeId,
        date: res.date,
        startSlot: res.startSlot,
        endSlot: res.endSlot,
        state: res.state,
        notes: res.notes || "",
        subject: res.subject || "",
        eventId: res.eventId || null,
        holderId: res.holderId,
      },
    });
  };

  const handleConfirm = async ({ date, startSlot, endSlot, state, notes, subject, eventId, notifyCollaboratorIds, override }: any) => {
    if (!modal) return;
    try {
      if (modal.kind === "new") {
        const res = (await api.post("/reservations", {
          placeId: modal.initial.placeId,
          date,
          startSlot,
          endSlot,
          state,
          notes,
          subject,
          eventId,
          notifyCollaboratorIds,
          override,
        })) as { reservation?: Record<string, unknown> } & Record<string, unknown>;
        // Optimistic insert + refresh
        setReservations((arr) => arr.concat([res.reservation || res]));
        setModal(null);
        await loadAll();
        bumpMut();
        toast && toast(t("places.reserved_ok", { fallback: "Reservation saved" }), "success");
      } else {
        const id = modal.initial.id;
        const res = modal.initial.holderId === currentUser
          ? await api.patch(`/reservations/${id}`, { date, startSlot, endSlot, state, notes, subject, eventId })
          : await api.patch(`/reservations/${id}`, { startSlot, endSlot, state, notes, subject, eventId });
        setReservations((arr) => arr.map((r) => (r.id === id ? res : r)));
        setModal(null);
        bumpMut();
        toast && toast(t("places.saved_ok", { fallback: "Reservation updated" }), "success");
      }
    } catch (e: any) {
      // Conflict → keep the modal open and surface the clashing booking
      // (holder / subject / notes) inline; it is the persistent failure UI.
      if (e && e.status === 409 && e.body && e.body.code === "reservation.conflict") {
        setModal((m: any) => ({ ...m, conflictPayload: e.body }));
        return;
      }
      if (e && e.status === 403) {
        const code = e.body && e.body.code;
        const reason =
          code === "reservation.override_forbidden" || code === "reservation.cross_user_edit"
            ? t("places.override_forbidden", {
                fallback: "You don't have authority to change another user's reservation.",
              })
            : t("places.forbidden", { fallback: "You don't have permission to do that." });
        toast && toast(reason, "error");
        return;
      }
      toast && toast(t("places.save_failed", { fallback: "Couldn't save the reservation — please try again." }), "error");
    }
  };

  const handleDelete = async () => {
    if (!modal || modal.kind !== "edit") return;
    askConfirm({
      // 161C §3
      title: t("confirm.cancel_reservation_title", { fallback: "Cancel the reservation?" }),
      message: t("places.confirm_cancel", { fallback: "Cancel this reservation?" }),
      onConfirm: async () => {
        try {
          await api.del(`/reservations/${modal.initial.id}`);
          setReservations((arr) => arr.filter((r) => r.id !== modal.initial.id));
          setModal(null);
          bumpMut();
          toast && toast(t("places.cancelled_ok", { fallback: "Reservation cancelled" }), "success");
        } catch (e: any) {
          const reason =
            e && e.status === 403
              ? t("places.cancel_forbidden", { fallback: "You don't have authority to cancel another user's reservation." })
              : t("places.cancel_failed", { fallback: "Couldn't cancel the reservation — please try again." });
          toast && toast(reason, "error");
        }
      },
    });
  };

  return (
    <div style={{ padding: 14, paddingBottom: 80 }}>
      {/* Category filter — segmented toggle, one button per real category.
          Selecting one scopes BOTH the overview and the detail grids to it. */}
      {grouped.length > 1 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
          {grouped.map(([code, list]: any) => (
            <button
              key={code}
              className="btn btn-ghost"
              type="button"
              onClick={() => setSelectedCat(code)}
              style={activeToggleStyle(code === effectiveCat)}
              title={labelFor(code)}
            >
              {labelFor(code)}{" "}
              <span style={{ opacity: 0.6, fontWeight: 400 }}>({list.length})</span>
            </button>
          ))}
        </div>
      )}
      {/* Section controls */}
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 14,
          alignItems: "center",
        }}
      >
        <div
          style={{
            position: "relative",
            flex: "1 1 220px",
            minWidth: 180,
            maxWidth: 360,
          }}
        >
          <Search size={12} style={{ position: "absolute", top: 9, left: 8, color: "var(--tm)" }} />
          <input
            className="field-input"
            placeholder={t("places.search", { fallback: "Search places…" })}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ paddingLeft: 26 }}
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              style={{ position: "absolute", top: 5, right: 4, background: "none", border: "none", cursor: "pointer", color: "var(--tm)" }}
              aria-label={t("common.clear", { fallback: "Clear" })}
            >
              <X size={12} />
            </button>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button
            className="btn btn-ghost"
            type="button"
            onClick={() => setDefaultState("booked")}
            style={activeToggleStyle(defaultState === "booked")}
            title={t("places.btn.book.tip", { fallback: "Set Book as default state for new reservations" })}
          >
            {t("places.btn.book", { fallback: "Book" })}
          </button>
          <button
            className="btn btn-ghost"
            type="button"
            onClick={() => setDefaultState("held")}
            style={activeToggleStyle(defaultState === "held")}
            title={t("places.btn.hold.tip", { fallback: "Set Hold as default state for new reservations" })}
          >
            {t("places.btn.hold", { fallback: "Put on hold" })}
          </button>
          <button
            className="btn btn-ghost"
            type="button"
            onClick={() => setShowOnlyMine((v) => !v)}
            style={activeToggleStyle(showOnlyMine)}
            title={t("places.btn.my.tip", { fallback: "Highlight my reservations" })}
          >
            {t("places.btn.my", { fallback: "My" })}
          </button>
        </div>
      </div>

      {loading && (
        <div style={{ fontSize: FS.s11, color: "var(--tm)" }}>{t("common.loading", { fallback: "Loading…" })}</div>
      )}

      {!loading && selectedPlaces.length > 0 && (
        <CollectiveOverview
          schedAxis={schedAxis}
          places={selectedPlaces}
          categoryLabel={labelFor(effectiveCat)}
          collaborators={co}
          currentUserId={currentUser}
          refreshSignal={mutCount}
          onReserveAt={onReserveAt}
          onResClick={onResClick}
          onJumpToPlace={onJumpToPlace}
        />
      )}

      {!loading && grouped.length === 0 && (
        <div style={{ fontSize: FS.s12, color: "var(--tm)", padding: 24, textAlign: "center" }}>
          {t("places.empty_firm", {
            fallback: "No places yet. An administrator can add them in Settings → Places.",
          })}
        </div>
      )}

      {grouped
        .filter(([catCode]: any) => catCode === effectiveCat)
        .map(([catCode, list]: any) => {
        const filtered = showOnlyMine
          ? list.filter((p: any) =>
              reservations.some(
                (r) => r.placeId === p.id && r.holderId === currentUser,
              ),
            )
          : list;
        if (filtered.length === 0) return null;
        return (
          <div key={catCode} style={{ marginBottom: 20 }}>
            <h3
              style={{
                fontSize: FS.s13,
                margin: "8px 0 10px",
                color: "var(--ts)",
                // Phase 25.1 r4#2: divider line under the category subtitle
                // removed (cluttered without purpose).
              }}
            >
              {labelFor(catCode)}{" "}
              <span style={{ color: "var(--tm)", fontWeight: 400, fontSize: FS.s11 }}>({filtered.length})</span>
            </h3>
            {filtered.map((p: any) => (
              <div key={p.id} id={"place-detail-" + p.id} style={{ scrollMarginTop: 12 }}>
                <PlaceGrid
                  place={p}
                  windowStart={roomStart(p.id)}
                  onWindowChange={(d: any) => setRoomStart(p.id, d)}
                  collaborators={co}
                  events={ag}
                  currentUserId={currentUser}
                  highlightOwn={showOnlyMine}
                  searchHitIds={searchHitIds}
                  refreshSignal={mutCount}
                  onPaintCommit={onPaintCommit}
                  onResClick={onResClick}
                  onOpenEvent={openLinkedEvent}
                />
              </div>
            ))}
          </div>
        );
      })}

      {!loading && places.length > 0 && (
        <PlacesActivityFeed reservations={reservations} places={places} collaborators={co} />
      )}

      {modal && (
        <ReserveModal
          initial={modal.initial}
          places={places}
          collaborators={co}
          events={ag}
          currentUserId={currentUser}
          defaultState={defaultState}
          isEdit={modal.kind === "edit"}
          conflictPayload={modal.conflictPayload}
          viewerCanOverride={viewerCanOverride}
          /* 124 §3 — the SAME callback the room grid already passes at :2050.
             One seam, two callers; nothing is built for this. */
          onOpenEvent={openLinkedEvent}
          onClose={() => setModal(null)}
          onConfirm={handleConfirm}
        />
      )}
      {modal && modal.kind === "edit" && (
        <div style={{ position: "fixed", bottom: 16, left: 16, zIndex: Z.picker }}>
          <button className="btn btn-danger" type="button" onClick={handleDelete}>
            {t("places.cancel_res", { fallback: "Cancel reservation" })}
          </button>
        </div>
      )}
    </div>
  );
}
